import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useEffect, useState, useCallback, useRef } from "react";
import api from "../../../../api/api";
import { useSelector } from "react-redux";

const PAGE_SIZE = 5; // pages per batch
const DEFAULT_ZOOM = 1.2;

const DocumentViewerPage = ({ page, index }) => {
    const contentType = page.content_type || "image/png";
    const isPdf = contentType.includes("pdf");
    const isHtml = contentType.includes("html");
    const isText = contentType.includes("text/plain");
    const isOctet = contentType === "application/octet-stream";

    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        if (isPdf && page.data) {
            try {
                const byteString = atob(page.data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const uintArray = new Uint8Array(arrayBuffer);
                for (let i = 0; i < byteString.length; i++) {
                    uintArray[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([uintArray], { type: contentType });
                const url = URL.createObjectURL(blob);
                setBlobUrl(url);
                return () => URL.revokeObjectURL(url);
            } catch (err) {
                console.error("Base64 render error:", err);
            }
        }
    }, [page.data, isPdf, contentType]);

    // For text-like content, decode base64 to string
    let textContent = null;
    if ((isHtml || isText || isOctet) && page.data) {
        try {
            textContent = atob(page.data);
        } catch (e) {
            console.error("Base64 decode error:", e);
        }
    }

    const src = isPdf
        ? (blobUrl || "")
        : (page.data ? `data:${contentType};base64,${page.data}` : page.href || "");

    return (
        <div className="flex justify-center w-full">
            {isPdf ? (
                src && (
                    <iframe
                        src={src}
                        title={`Document page ${page.pageid || index + 1}`}
                        className="w-full rounded-lg border-none"
                        style={{ height: "80vh" }}
                    />
                )
            ) : (isHtml && textContent) ? (
                <div
                    className="w-full p-6 bg-white rounded-lg"
                    style={{ fontSize: '14px', lineHeight: '1.6', color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: textContent }}
                />
            ) : ((isText || isOctet) && textContent) ? (
                <div
                    className="w-full p-6 bg-gray-50 rounded-lg"
                    style={{ fontSize: '14px', lineHeight: '1.8', color: '#333', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                >
                    {textContent}
                </div>
            ) : (
                <img
                    src={src}
                    alt={`Document page ${page.pageid || index + 1}`}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                    }}
                    loading="lazy"
                    onError={() => console.error(`Failed to load page ${page.pageid}`)}
                />
            )}
        </div>
    );
};

/* Small thumbnail for the sidebar */
const PageThumbnail = ({ page, index, isActive, onClick }) => {
    const contentType = page.content_type || "image/png";
    const isPdf = contentType.includes("pdf");
    const isHtml = contentType.includes("html");
    const isText = contentType.includes("text/plain") || contentType === "application/octet-stream";
    const isImage = !isPdf && !isHtml && !isText;

    const src = isImage && page.data
        ? `data:${contentType};base64,${page.data}`
        : null;

    return (
        <button
            onClick={onClick}
            className={`flex-shrink-0 rounded-lg border-2 overflow-hidden transition-all ${
                isActive
                    ? "border-[#5C8496] shadow-md ring-2 ring-[#5C8496]/30"
                    : "border-gray-200 hover:border-gray-400"
            }`}
            style={{ width: 80, height: 100 }}
            title={`Page ${index + 1}`}
        >
            {src ? (
                <img
                    src={src}
                    alt={`Page ${index + 1}`}
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center gap-1">
                    <span className="text-gray-400 text-lg">
                        {isPdf ? "\uD83D\uDCC4" : isHtml ? "\uD83C\uDF10" : "\uD83D\uDCDD"}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium">Page {index + 1}</span>
                </div>
            )}
        </button>
    );
};


const ProcedureDocument = ({ isProcedureDocumentOpen, subview, setIsProcedureDocumentOpen, procedure }) => {
    const [pages, setPages] = useState([]);       // [{pageid, content_type, data (base64)}]
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const [activePage, setActivePage] = useState(0);
    // AI-generated summary shown as a header above the PDF pages. Fetched
    // lazily from the dedicated /summary endpoint (cache-only, no LLM cost).
    const [aiSummary, setAiSummary] = useState(null);
    const [aiLabel, setAiLabel] = useState(null);
    const pageRefs = useRef([]);
    const patientId = procedure.patientid;
    const practiseid = window.getPracticeId();
    const userConfig = useSelector((state) => state.userconfig);

    const fetchDocument = useCallback(async (offset = 0, append = false) => {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        try {
            let response;

            const isClinical =
                procedure?.documentclass === "CLINICALDOCUMENT" ||
                (!procedure?.encounterdocumentid && !procedure?.imagingresultid && procedure?.clinicaldocumentid);

            if (isClinical) {
                response = await api.post(
                    `/v1/fhir-mirror/${practiseid}/clinical-documents-by-id`,
                    {
                        patient_id: patientId,
                        // seeded users may have no config.departmentid — never
                        // drop the key (the API 422s on missing fields)
                        department_id: userConfig?.config?.departmentid ?? 1,
                        clinicaldocument_id: procedure.clinicaldocumentid,
                    }
                );
            } else {
                const docId = procedure.imagingresultid || procedure.encounterdocumentid;

                const category =
                    procedure.imagingresultid ? "Procedures" : "ProgressNotes";

                if (!docId) {
                    console.error("No document ID available for this document", procedure);
                    setPages([]);
                    setIsLoading(false);
                    return;
                }

                response = await api.post(
                    `/v1/fhir-mirror/${practiseid}/patient/${patientId}/procedures/image-result`,
                    {
                        patientid: patientId,
                        imagingresultid: docId,
                        fhir_resource_id: procedure.fhir_resource_id || null,
                        category,
                        page_offset: offset,
                        page_limit: PAGE_SIZE,
                    }
                );
            }

            const docData = response?.data || response;
            const firstEntry = Array.isArray(docData) ? docData[0] : docData;
            const fetchedPages = firstEntry?.pages || [];
            const total = firstEntry?.total_pages || fetchedPages.length;

            if (append) {
                setPages((prev) => [...prev, ...fetchedPages]);
            } else {
                setPages(fetchedPages);
            }
            setTotalPages(total);
        } catch (error) {
            console.error("Error fetching document:", error.response ? error.response.data : error);
            if (!append) setPages([]);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [subview, procedure, patientId, practiseid, userConfig]);

    useEffect(() => {
        if (
            isProcedureDocumentOpen &&
            patientId &&
            (procedure.encounterdocumentid || procedure.imagingresultid || procedure.clinicaldocumentid)
        ) {
            fetchDocument(0, false);
            // Lazy AI-summary fetch — read-only cache lookup, no LLM cost.
            // Category mirrors what /sync/0 writes into documents_cache.
            const cat = procedure.documentclass === "CLINICALDOCUMENT" || procedure.clinicaldocumentid
                ? "clinicaldocument"
                : procedure.documentclass === "ENCOUNTERDOCUMENT" || procedure.encounterdocumentid
                    ? "encounterdocument"
                    : "imagingresult";
            const docId = procedure.imagingresultid || procedure.encounterdocumentid || procedure.clinicaldocumentid;
            if (docId) {
                api.get(`/v1/${practiseid}/patients/${patientId}/documents/${cat}/${docId}/summary`)
                    .then((res) => {
                        const d = res?.data?.data || res?.data || {};
                        if (d.summary) setAiSummary(d.summary);
                        if (d.label) setAiLabel(d.label);
                    })
                    .catch((err) => {
                        // Cache miss / 404 is fine — just skip the AI summary header.
                        console.debug("[ProcedureDocument] no cached AI summary:", err?.response?.status);
                    });
            }
        }
        // Reset when closed
        if (!isProcedureDocumentOpen) {
            setPages([]);
            setTotalPages(0);
            setZoom(DEFAULT_ZOOM);
            setActivePage(0);
            setAiSummary(null);
            setAiLabel(null);
        }
    }, [isProcedureDocumentOpen]);

    const handleLoadMore = () => {
        if (!isLoadingMore && pages.length < totalPages) {
            fetchDocument(pages.length, true);
        }
    };

    const scrollToPage = (index) => {
        setActivePage(index);
        setZoom(1); // zoom to 100% on click
        const el = pageRefs.current[index];
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    if (!isProcedureDocumentOpen) return null;

    const hasMore = pages.length < totalPages;
    const showThumbnails = pages.length > 1;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50" onClick={() => setIsProcedureDocumentOpen(false)}>
            <div className="absolute inset-0 bg-black/50 opacity-50"></div>
            <div
                className="relative z-10 h-[95vh] rounded-lg bg-white shadow-custom flex flex-col overflow-hidden"
                style={{ width: '95vw', maxWidth: '1400px' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header toolbar */}
                <div className="p-3 w-full flex justify-between items-center border-b border-gray-200 flex-shrink-0">
                    <div className="font-medium text-[#1d2951] text-lg tracking-[0] leading-6 whitespace-nowrap">
                        Result {totalPages > 0 && <span className="text-sm text-gray-400 font-normal ml-2">({pages.length} of {totalPages} pages)</span>}
                    </div>
                    <div className="flex items-center gap-1">
                        <IconButton
                            size="small"
                            onClick={() => setZoom(z => Math.max(0.25, +(z - 0.2).toFixed(2)))}
                            title="Zoom out"
                            disabled={zoom <= 0.25}
                        >
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                        <span className="text-xs text-gray-500 min-w-[40px] text-center select-none font-medium">{Math.round(zoom * 100)}%</span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(2)))}
                            title="Zoom in"
                            disabled={zoom >= 3}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                        <div className="w-px h-5 bg-gray-200 mx-1" />
                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={() => setIsProcedureDocumentOpen(false)}
                            style={{ color: '#C7C7CB' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>

                {/* Body: thumbnail sidebar + main content */}
                <div className="flex flex-1 min-h-0 overflow-hidden">
                    {/* Thumbnail sidebar */}
                    {showThumbnails && (
                        <div
                            className="flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto p-3 flex flex-col gap-3 items-center"
                            style={{ width: 110 }}
                        >
                            {pages.map((page, i) => (
                                <div key={page.pageid || i} className="flex flex-col items-center gap-1">
                                    <PageThumbnail
                                        page={page}
                                        index={i}
                                        isActive={activePage === i}
                                        onClick={() => scrollToPage(i)}
                                    />
                                    <span className={`text-[10px] font-medium ${activePage === i ? "text-[#5C8496]" : "text-gray-400"}`}>
                                        {i + 1}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Main document area */}
                    <div className="flex-1 overflow-auto p-4">
                        {/* AI-generated summary header (cache-only fetch) —
                            shown before the document pages so providers can
                            read the gist before scrolling the PDF. Falls
                            through silently when no cached summary exists. */}
                        {aiSummary && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        AI Generated Summary
                                    </span>
                                    {aiLabel && (
                                        <span className="text-sm font-medium text-[#1d2951]">
                                            {aiLabel}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-5">
                                    {aiSummary}
                                </p>
                                <p className="text-[10px] text-gray-500 italic mt-2">
                                    AI Generated Summary may contain inaccuracies.
                                </p>
                            </div>
                        )}
                        <div
                            className="space-y-4 w-full"
                            style={{
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top center',
                                transition: 'transform 0.15s ease',
                            }}
                        >
                            {isLoading ? (
                                <div className="text-center text-gray-500 h-full">
                                    <div className="mt-20">
                                        <CircularProgress />
                                    </div>
                                </div>
                            ) : pages.length > 0 ? (
                                <>
                                    {pages.map((page, index) => (
                                        <div
                                            key={page.pageid || index}
                                            ref={(el) => (pageRefs.current[index] = el)}
                                        >
                                            <DocumentViewerPage page={page} index={index} />
                                        </div>
                                    ))}
                                    {hasMore && (
                                        <div className="flex justify-center py-4">
                                            <button
                                                onClick={handleLoadMore}
                                                disabled={isLoadingMore}
                                                className="px-6 py-2.5 rounded-xl bg-[#5C8496] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                                            >
                                                {isLoadingMore ? (
                                                    <span className="flex items-center gap-2">
                                                        <CircularProgress size={16} color="inherit" />
                                                        Loading...
                                                    </span>
                                                ) : (
                                                    `Load More (${totalPages - pages.length} remaining)`
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center text-gray-500">No pages available.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcedureDocument;
